/**
 * The motor and its indicator lights: power, 33/45, the pitch fader and
 * whether the platter is running.
 *
 * A running platter follows the speed controls immediately, whether or not
 * anything is playing through it — so every control re-derives the target
 * speed rather than leaving it to whoever calls `setSpinning` next.
 */

import type * as THREE from 'three';
import { RPM_33, RPM_45 } from '../geometry/dims.ts';
import { spinRate, zForPitch } from '../geometry/transport.ts';
import { clamp } from '../lib/math.ts';
import type { DeckState } from './deck-state.ts';
import { setLed } from './deck-state.ts';
import { ACCENT, ACCENT_OFF } from './materials.ts';
import type { DeckLeds } from './parts/controls.ts';
import type { ProgressRing } from './progress-ring.ts';

/** The fader's travel, in percent either side of zero. */
const PITCH_RANGE = 8;

export interface Motor {
  setPowered(on: boolean): void;
  setRPM(rpm: number): void;
  setPitch(pct: number): number;
  setSpinning(on: boolean): void;
}

export interface MotorParts {
  leds: DeckLeds;
  faderKnob: THREE.Group;
  ring: ProgressRing;
  /** Called when the deck is switched off, to drop the needle back up. */
  onPowerOff: () => void;
}

export function createMotor(state: DeckState, parts: MotorParts): Motor {
  const { leds, faderKnob, ring, onPowerOff } = parts;
  const targetRate = (): number => spinRate(state.rpm, state.pitch);
  const retarget = (): void => {
    if (state.targetSpin > 0) state.targetSpin = targetRate();
  };

  return {
    setPowered(on) {
      state.powered = on;
      setLed(leds.power, on, ACCENT, ACCENT_OFF);
      if (!on) {
        state.targetSpin = 0;
        onPowerOff();
      }
      ring.setVisible(on);
    },

    setRPM(rpm) {
      const fast = rpm === RPM_45;
      state.rpm = fast ? RPM_45 : RPM_33;
      setLed(leds.s33, !fast, ACCENT, ACCENT_OFF);
      setLed(leds.s45, fast, ACCENT, ACCENT_OFF);
      retarget();
    },

    setPitch(pct) {
      state.pitch = clamp(pct, -PITCH_RANGE, PITCH_RANGE);
      faderKnob.position.z = zForPitch(state.pitch);
      retarget();
      return state.pitch;
    },

    setSpinning(on) {
      const running = on && state.powered;
      state.targetSpin = running ? targetRate() : 0;
      setLed(leds.start, running, ACCENT, ACCENT_OFF, 1.3);
    },
  };
}
